// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Trophy, Share2, Download, RefreshCw, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import BracketVisualizer from '@/components/tournaments/BracketVisualizer';
import StandingsTable from '@/components/tournaments/StandingsTable';
import NotificationSignupModal from '@/components/tournaments/NotificationSignupModal';
import { parseLocalDate } from '@/utils/formatters';

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  status: string;
  start_date: string;
  end_date: string;
  events?: {
    sports?: {
      name: string;
    };
  };
}

interface TournamentMatch {
  id: string;
  tournament_id: string;
  round_number: number;
  match_number: number;
  bracket_type: string;
  team1_id?: string;
  team2_id?: string;
  team1_name?: string;
  team2_name?: string;
  team1_score?: number;
  team2_score?: number;
  winner_id?: string;
  status: string;
  scheduled_time?: string;
  venue_id?: string;
  court_field?: string;
}

const TournamentBracketPage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTournamentData();
      
      // Set up real-time subscription for match updates
      const channel = supabase
        .channel('tournament-matches')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'tournament_matches',
            filter: `tournament_id=eq.${id}`
          },
          () => {
            fetchMatches();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [id]);

  const fetchTournamentData = async () => {
    try {
      await Promise.all([fetchTournament(), fetchMatches()]);
    } catch (error) {
      console.error('Error fetching tournament data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournament = async () => {
    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        events(
          sports(name)
        )
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    setTournament(data);
  };

  const fetchMatches = async () => {
    const { data, error } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', id)
      .order('round_number')
      .order('match_number');

    if (error) throw error;
    setMatches(data || []);
  };

  const refreshBracket = async () => {
    setRefreshing(true);
    try {
      await fetchMatches();
      toast({
        title: "Bracket Updated",
        description: "Latest match results have been loaded",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh bracket",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const shareBracket = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${tournament?.name} - Tournament Bracket`,
          text: `Check out the live bracket for ${tournament?.name}`,
          url: url,
        });
      } catch (error) {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied!",
          description: "Bracket link copied to clipboard",
        });
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "Bracket link copied to clipboard",
      });
    }
  };

  const printBracket = () => {
    window.print();
  };

  const getTournamentStatus = (tournament: Tournament) => {
    const now = new Date();
    const startDate = parseLocalDate(tournament.start_date);
    const endDate = parseLocalDate(tournament.end_date);
    
    if (startDate > now) return { label: 'Upcoming', variant: 'secondary' };
    if (startDate <= now && endDate >= now) return { label: 'Live', variant: 'destructive' };
    return { label: 'Completed', variant: 'outline' };
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Loading bracket...</div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center py-12">
          <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Tournament not found</h3>
          <p className="text-muted-foreground mb-4">
            The tournament bracket you're looking for doesn't exist.
          </p>
          <Button asChild>
            <Link to="/tournaments">Browse Tournaments</Link>
          </Button>
        </div>
      </div>
    );
  }

  const status = getTournamentStatus(tournament);
  const isRoundRobin = tournament.tournament_type === 'round_robin';

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/tournaments/${tournament.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Tournament
              </Link>
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={status.variant as any}>
              {status.label}
            </Badge>
            {tournament.events?.sports && (
              <Badge variant="outline">{tournament.events.sports.name}</Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold">{tournament.name}</h1>
          <p className="text-lg text-muted-foreground">Tournament Bracket</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowNotificationModal(true)}
            variant="outline"
            size="sm"
          >
            <Bell className="h-4 w-4 mr-2" />
            Get Notified
          </Button>
          <Button
            onClick={refreshBracket}
            variant="outline"
            size="sm"
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={shareBracket} variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          <Button onClick={printBracket} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Bracket Content */}
      <div className="space-y-6">
        {isRoundRobin ? (
          <Card>
            <CardHeader>
              <CardTitle>Round Robin Standings</CardTitle>
            </CardHeader>
            <CardContent>
              <StandingsTable matches={matches} />
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="bracket" className="w-full">
            <TabsList>
              <TabsTrigger value="bracket">Bracket</TabsTrigger>
              {tournament.tournament_type === 'double_elimination' && (
                <>
                  <TabsTrigger value="winners">Winners Bracket</TabsTrigger>
                  <TabsTrigger value="losers">Losers Bracket</TabsTrigger>
                </>
              )}
              {tournament.tournament_type === 'pool_play_playoffs' && (
                <>
                  <TabsTrigger value="pools">Pool Play</TabsTrigger>
                  <TabsTrigger value="playoffs">Playoffs</TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="bracket">
              <Card>
                <CardContent className="p-6">
                  <BracketVisualizer
                    matches={matches}
                    tournamentType={tournament.tournament_type}
                    bracketType="all"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {tournament.tournament_type === 'double_elimination' && (
              <>
                <TabsContent value="winners">
                  <Card>
                    <CardHeader>
                      <CardTitle>Winners Bracket</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <BracketVisualizer
                        matches={matches.filter(m => m.bracket_type === 'winners')}
                        tournamentType={tournament.tournament_type}
                        bracketType="winners"
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="losers">
                  <Card>
                    <CardHeader>
                      <CardTitle>Losers Bracket</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <BracketVisualizer
                        matches={matches.filter(m => m.bracket_type === 'losers')}
                        tournamentType={tournament.tournament_type}
                        bracketType="losers"
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
              </>
            )}

            {tournament.tournament_type === 'pool_play_playoffs' && (
              <>
                <TabsContent value="pools">
                  <Card>
                    <CardHeader>
                      <CardTitle>Pool Play Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StandingsTable 
                        matches={matches.filter(m => m.bracket_type === 'pool')} 
                        showPools={true}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="playoffs">
                  <Card>
                    <CardHeader>
                      <CardTitle>Playoff Bracket</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <BracketVisualizer
                        matches={matches.filter(m => m.bracket_type === 'final')}
                        tournamentType={tournament.tournament_type}
                        bracketType="final"
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
              </>
            )}
          </Tabs>
        )}

        {matches.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Bracket Available</h3>
              <p className="text-muted-foreground">
                The tournament bracket will be available once teams are registered and the bracket is generated.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Notification Signup Modal */}
      <NotificationSignupModal
        open={showNotificationModal}
        onOpenChange={setShowNotificationModal}
        tournamentId={tournament.id}
        tournamentName={tournament.name}
      />
    </div>
  );
};

export default TournamentBracketPage;